'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { useFreemium } from '@/lib/freemium-context';
import { FREE_PLAN, PRO_OPTIMAL } from '@/lib/credit-costs';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  Upload, FileText, Loader2, RotateCcw, Tags, Brain, X,
  ClipboardPaste, BookOpen, PenLine, RefreshCw, FileUp, CheckCircle2, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToExcel, ExportExcelError } from '@/lib/export-excel';
import { friendlyError } from '@/lib/error-messages';
import { AUTH_RESUME_SETTLE_MS } from '@/lib/auth-resume';
import { useModuleDocuments } from '@/hooks/use-module-documents';
import ModuleHistoryPanel from '@/components/transqual/ModuleHistoryPanel';
import { loadPersistedResult, savePersistedResult, clearPersistedResult } from '@/lib/result-persistence';
import { CreditCostBanner } from '@/components/CreditCostBanner';
import UpgradeBlockedCTA from '@/components/UpgradeBlockedCTA';

// ─── Stepper ─────────────────────────────────────────────────────────────────

const M_COLOR = '#1a6060';
const M_BG = '#e0f5f5';
const M_STEPS = [
  { name: 'Données', hint: 'Verbatims source' },
  { name: 'Paramètres', hint: 'Approche & thèmes' },
  { name: 'Résultats', hint: 'Lancer & télécharger' },
];

// ─── Types ───────────────────────────────────────────────────────────────────

type Approach = 'inductive' | 'deductive' | 'mixed';
type ThemeCount = '3-5' | '5-8' | '8-12' | 'libre';

interface UploadedFile {
  id: string;
  name: string;
  content: string;
  size: number;
}

interface AnalysisResponse {
  success: boolean;
  text: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const APPROACH_OPTIONS: { value: Approach; label: string; description: string }[] = [
  { value: 'inductive', label: 'Inductive (codes émergents)', description: 'Les thèmes émergent naturellement des données' },
  { value: 'deductive', label: 'Déductive (grille imposée)', description: "Une grille de codage préétablie est appliquée aux données" },
  { value: 'mixed', label: 'Mixte', description: 'Combinaison d\'une grille préétablie et de thèmes émergents' },
];

const THEME_COUNT_OPTIONS: { value: ThemeCount; label: string }[] = [
  { value: '3-5', label: '3 – 5 thèmes' },
  { value: '5-8', label: '5 – 8 thèmes' },
  { value: '8-12', label: '8 – 12 thèmes' },
  { value: 'libre', label: 'Libre' },
];

// Étapes simulées de la codification — la barre avance (réassurance) même si
// le serveur ne renvoie pas de progression réelle. Les % plafonnent à 95 %
// jusqu'à la fin réelle (qui passe à 100 %). Délais en ms depuis le lancement.
const CODING_STAGES: { pct: number; label: string; delayMs: number }[] = [
  { pct:  8, label: 'Lecture des verbatims…',                       delayMs:      0 },
  { pct: 18, label: 'Préparation du corpus…',                       delayMs:   4000 },
  { pct: 30, label: 'Identification des thèmes émergents…',         delayMs:  20000 },
  { pct: 45, label: 'Construction de l\'arbre de codes…',           delayMs:  60000 },
  { pct: 60, label: 'Extraction des citations représentatives…',    delayMs: 150000 },
  { pct: 72, label: 'Codification des entretiens…',                 delayMs: 300000 },
  { pct: 82, label: 'Calcul des fréquences…',                       delayMs: 480000 },
  { pct: 90, label: 'Assemblage de la matrice…',                    delayMs: 660000 },
  { pct: 95, label: 'Finalisation…',                                delayMs: 840000 },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

interface CodificationSummary {
  fichiers: number; themes: number; sousThemes: number; extraits: number;
}

function computeSummary(text: string, verbatimCount: number): CodificationSummary {
  const match = text.match(/<!--QUALYSE_MATRIX\s*([\s\S]*?)-->/);
  if (match) {
    try {
      const json = JSON.parse(match[1].trim()) as {
        grille: { sousThemes: unknown[] }[];
        matrice: { extraits: Record<string, string> }[];
      };
      const themes = json.grille?.length ?? 0;
      const sousThemes = json.grille?.reduce((s, t) => s + (t.sousThemes?.length ?? 0), 0) ?? 0;
      const extraits = json.matrice?.reduce((s, row) => s + Object.values(row.extraits ?? {}).filter(v => v.trim()).length, 0) ?? 0;
      return { fichiers: json.matrice?.length ?? verbatimCount, themes, sousThemes, extraits };
    } catch { /* fall through */ }
  }
  const themes = (text.match(/^[A-Z]\.\s/gm) ?? []).length;
  const sousThemes = (text.match(/^[A-Z]\.\d+\s/gm) ?? []).length;
  const extraits = (text.match(/«[^»]+»/g) ?? []).length;
  return { fichiers: verbatimCount, themes, sousThemes, extraits };
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function Module02({ compact, onHasResult }: { compact?: boolean; onHasResult?: (has: boolean) => void } = {}) {
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [pastedText, setPastedText] = useState('');
  const [approach, setApproach] = useState<Approach>('inductive');
  const [deductiveGrid, setDeductiveGrid] = useState('');
  const [themeCount, setThemeCount] = useState<ThemeCount>('libre');
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [result, setResult] = useState<string>('');
  const [activeInputMode, setActiveInputMode] = useState<'upload' | 'paste'>('upload');
  const [guideFile, setGuideFile] = useState<{ name: string; content: string } | null>(null);
  const [isParsingGuide, setIsParsingGuide] = useState(false);
  const [isEditingCodes, setIsEditingCodes] = useState(false);
  const [editedCodes, setEditedCodes] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const guideInputRef = useRef<HTMLInputElement>(null);
  const analyzeAbortRef = useRef<AbortController | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  // Progression « par étapes » simulée pendant la codification (le endpoint
  // /api/analyze ne streame pas de %). Évite la barre figée qui laisse croire
  // que c'est planté. Purement cosmétique côté client.
  const [codingProgress, setCodingProgress] = useState<{ pct: number; label: string } | null>(null);

  const { limits, incrementLimit, openUpgrade, openAuthGate, isAuthenticated } = useFreemium();
  // Sauvegarde auto + historique 48 h (anti-perte).
  const { history, saveDocument } = useModuleDocuments('codification', isAuthenticated);
  // Plan gratuit : bloqué si un autre module a déjà été utilisé (verrou) OU si
  // le solde ne couvre plus le forfait codification (90 crédits).
  // IMPORTANT : ne s'applique qu'aux utilisateurs CONNECTÉS. Un visiteur
  // anonyme (credits=0 par défaut) doit voir « Lancer » pour déclencher la
  // pop-up d'inscription — sinon il est bloqué à tort.
  //
  // Deux causes distinctes → deux CTA distincts :
  //  - lockedByOther → autre module gratuit déjà utilisé → « Passer en Pro »
  //  - exhausted     → crédits réellement insuffisants → « Limite atteinte »
  const codifLockedByOther =
    isAuthenticated && !limits.is_pro &&
    limits.free_module_used !== null && limits.free_module_used !== 'codification';
  const codifExhausted =
    isAuthenticated && !limits.is_pro && !codifLockedByOther &&
    limits.credits < FREE_PLAN.codingFlat;
  const codifBlocked = codifLockedByOther || codifExhausted;
  // Conseil d'optimisation : on suggère discrètement à l'utilisateur d'ajouter
  // plus d'entretiens pour rentabiliser le forfait (90 crédits free / 154 Pro).
  const tipMaxEntretiens = limits.is_pro ? PRO_OPTIMAL.codingMaxEntretiens : FREE_PLAN.maxEntretiens;
  const tipFlatCost = limits.is_pro ? 154 : FREE_PLAN.codingFlat;
  const showOptimizationTip =
    files.length >= 1 &&
    (limits.is_pro
      ? files.length < PRO_OPTIMAL.suggestThreshold // 1-9 entretiens
      : files.length < FREE_PLAN.maxEntretiens);    // 1-4 entretiens

  // Lancement en attente : un visiteur non connecté a cliqué sur « Lancer la
  // codification », la popup d'inscription s'est affichée. Une fois connecté,
  // on relance — les fichiers/verbatims déjà chargés restent en mémoire.
  const [pendingAuthLaunch, setPendingAuthLaunch] = useState(false);

  const showDeductiveGrid = approach === 'deductive' || approach === 'mixed';
  const hasVerbatims = files.length > 0 || pastedText.trim().length > 0;
  const isUploading = uploadProgress !== null && uploadProgress.done < uploadProgress.total;
  const uploadAllDone = uploadProgress !== null && uploadProgress.done === uploadProgress.total;

  useEffect(() => { onHasResult?.(result.length > 0) }, [result, onHasResult]);

  // Persistance localStorage — clé normalisée qualyse_module03_result.
  // Migration depuis l'ancienne clé qualyse_codification_result si présente.
  const RESULT_STORAGE_KEY = 'qualyse_module03_result';
  const LEGACY_STORAGE_KEY = 'qualyse_codification_result';
  const [restoredFromCache, setRestoredFromCache] = useState(false);
  // Restauration au mount
  useEffect(() => {
    const saved = loadPersistedResult<{ result: string; verbatimCount?: number }>(
      RESULT_STORAGE_KEY,
      LEGACY_STORAGE_KEY,
    );
    if (saved) {
      // Tolérance à l'ancienne forme : le payload pouvait être juste une string brute.
      const data = saved.data;
      if (typeof data === 'string') {
        setResult(data);
      } else if (data?.result) {
        setResult(data.result);
      }
      setRestoredFromCache(true);
    }
  }, []);
  // Sauvegarde dès que result devient non vide
  useEffect(() => {
    if (!result) return;
    savePersistedResult(RESULT_STORAGE_KEY, { result, verbatimCount: files.length || 0 }, LEGACY_STORAGE_KEY);
  }, [result, files.length]);

  const dismissRestoredBanner = useCallback(() => {
    setRestoredFromCache(false);
    clearPersistedResult(RESULT_STORAGE_KEY, LEGACY_STORAGE_KEY);
    setResult('');
    toast.info('Résultat précédent effacé.');
  }, []);

  const collectVerbatims = useCallback((): string[] => {
    const verbatims: string[] = [];
    files.forEach((file) => { if (file.content.trim()) verbatims.push(file.content.trim()); });
    if (pastedText.trim()) {
      const segments = pastedText.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
      verbatims.push(...segments);
    }
    return verbatims;
  }, [files, pastedText]);

  const collectVerbatimLabels = useCallback((): string[] => {
    const labels: string[] = [];
    files.forEach((file) => { if (file.content.trim()) labels.push(file.name.replace(/\.[^.]+$/, '')); });
    if (pastedText.trim()) {
      const segments = pastedText.split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
      segments.forEach((_, i) => labels.push(`Verbatim ${files.length + i + 1}`));
    }
    return labels;
  }, [files, pastedText]);

  const extractFileText = useCallback(async (file: File): Promise<string> => {
    const name = file.name.toLowerCase();
    if (name.endsWith('.docx') || name.endsWith('.doc') || name.endsWith('.pdf')) {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/parse-file', { method: 'POST', body: formData });
      if (!res.ok) throw new Error(`Erreur serveur (${res.status})`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      return data.text as string;
    }
    return file.text();
  }, []);

  const handleGuideUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsParsingGuide(true);
    try {
      const text = await extractFileText(file);
      setGuideFile({ name: file.name, content: text });
      toast.success(`Guide importé : ${file.name}`);
    } catch { toast.error(`Impossible de lire le guide : ${file.name}`); }
    finally { setIsParsingGuide(false); if (guideInputRef.current) guideInputRef.current.value = ''; }
  }, [extractFileText]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;
    if (!limits.is_pro && files.length >= FREE_PLAN.maxEntretiens) {
      toast.error(`Votre accès gratuit permet de codifier ${FREE_PLAN.maxEntretiens} entretiens maximum.`);
      openUpgrade(); return;
    }
    const slotsLeft = limits.is_pro ? Infinity : Math.max(0, FREE_PLAN.maxEntretiens - files.length);
    const toProcess = slotsLeft === Infinity ? selected.length : Math.min(selected.length, slotsLeft);
    if (toProcess === 0) return;

    setUploadProgress({ done: 0, total: toProcess });
    const newFiles: UploadedFile[] = [];
    for (let i = 0; i < toProcess; i++) {
      const file = selected[i];
      try {
        const text = await extractFileText(file);
        newFiles.push({ id: generateId(), name: file.name, content: text, size: file.size });
      } catch { toast.error(`Impossible de lire le fichier ${file.name}`); }
      setUploadProgress({ done: i + 1, total: toProcess });
    }
    if (!limits.is_pro && selected.length > toProcess) {
      toast.warning(`Plan gratuit : seuls ${toProcess} entretien(s) supplémentaire(s) ont été ajoutés (plafond ${FREE_PLAN.maxEntretiens}).`);
    }
    if (newFiles.length > 0) setFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [extractFileText, limits.is_pro, files.length, openUpgrade]);

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => { const file = prev.find((f) => f.id === id); if (file) toast.info(`Fichier "${file.name}" supprimé`); return prev.filter((f) => f.id !== id); });
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation();
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length === 0) return;
    if (!limits.is_pro && files.length >= FREE_PLAN.maxEntretiens) { toast.error(`Votre accès gratuit permet de codifier ${FREE_PLAN.maxEntretiens} entretiens maximum.`); openUpgrade(); return; }
    const slotsLeft = limits.is_pro ? Infinity : Math.max(0, FREE_PLAN.maxEntretiens - files.length);
    const validFiles: File[] = [];
    for (let i = 0; i < droppedFiles.length; i++) {
      if (droppedFiles[i].name.match(/\.(txt|md|docx|doc|pdf)$/i)) validFiles.push(droppedFiles[i]);
      else toast.warning(`Fichier "${droppedFiles[i].name}" ignoré (format non supporté)`);
    }
    const toProcess = slotsLeft === Infinity ? validFiles.length : Math.min(validFiles.length, Math.max(0, slotsLeft));
    if (toProcess === 0) return;

    setUploadProgress({ done: 0, total: toProcess });
    const newFiles: UploadedFile[] = [];
    for (let i = 0; i < toProcess; i++) {
      try {
        const text = await extractFileText(validFiles[i]);
        newFiles.push({ id: generateId(), name: validFiles[i].name, content: text, size: validFiles[i].size });
      } catch { toast.error(`Impossible de lire le fichier ${validFiles[i].name}`); }
      setUploadProgress({ done: i + 1, total: toProcess });
    }
    if (newFiles.length > 0) setFiles((prev) => [...prev, ...newFiles]);
  }, [extractFileText, limits.is_pro, files.length, openUpgrade]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); }, []);

  const handleAnalyze = useCallback(async () => {
    const verbatims = collectVerbatims();
    const verbatimLabels = collectVerbatimLabels();
    if (verbatims.length === 0) { toast.error('Veuillez fournir au moins un verbatim avant de lancer la codification.'); return; }
    // Inscription obligatoire avant tout traitement : on affiche la popup et on
    // garde le lancement en attente — les verbatims déjà chargés restent intacts.
    if (!isAuthenticated) {
      setPendingAuthLaunch(true);
      openAuthGate();
      return;
    }
    if (codifBlocked) { openUpgrade(); return; }
    if (approach === 'deductive' && !deductiveGrid.trim() && !guideFile) {
      toast.error('Veuillez saisir vos codes ou importer un guide d\'entretien pour l\'approche déductive.'); return;
    }
    setIsLoading(true); setIsEditingCodes(false); setResult(''); setRestoredFromCache(false);
    // Permet d'annuler la requête en cours (bouton « Annuler »).
    const controller = new AbortController();
    analyzeAbortRef.current = controller;
    // Progression simulée : la barre avance par étapes pendant l'attente.
    setCodingProgress({ pct: CODING_STAGES[0].pct, label: CODING_STAGES[0].label });
    const stageTimers: ReturnType<typeof setTimeout>[] = [];
    CODING_STAGES.forEach(({ pct, label, delayMs }) => {
      if (delayMs > 0) stageTimers.push(setTimeout(() => setCodingProgress({ pct, label }), delayMs));
    });
    try {
      const body: Record<string, unknown> = { type: 'coding', verbatims, verbatimLabels, approach: approach === 'mixed' ? 'mixte' : approach, themeCount };
      if (showDeductiveGrid) {
        let combined = deductiveGrid.trim();
        if (guideFile?.content) combined += (combined ? '\n\n--- Guide d\'entretien ---\n' : '') + guideFile.content;
        if (combined) body.deductiveGrid = combined;
      }
      const response = await fetch('/api/analyze', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null) as { error?: string } | null;
        throw new Error(errorData?.error || `Erreur serveur (${response.status})`);
      }
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        fullText += decoder.decode(value, { stream: true });
      }
      fullText += decoder.decode();
      if (fullText.trim()) {
        setResult(fullText);
        await incrementLimit('codification_count', files.length || 1);
        // Sauvegarde automatique (anti-perte + historique 48 h). Non bloquant.
        const docTitle = files.length > 0
          ? `${files.length} entretien${files.length > 1 ? 's' : ''}`
          : 'Codification';
        void saveDocument(docTitle, fullText);
        toast.success('Codification thématique terminée avec succès.');
      } else { throw new Error('Réponse vide du serveur.'); }
    } catch (error) {
      // Annulation volontaire de l'utilisateur → message neutre, pas une erreur.
      if (controller.signal.aborted || (error instanceof Error && error.name === 'AbortError')) {
        toast.info('Codification annulée.');
      } else {
        toast.error(friendlyError(error, 'processing'));
      }
      setResult('');
    } finally {
      stageTimers.forEach(clearTimeout);
      setCodingProgress(null);
      setIsLoading(false);
      analyzeAbortRef.current = null;
    }
  }, [collectVerbatims, collectVerbatimLabels, approach, themeCount, showDeductiveGrid, deductiveGrid, guideFile, codifBlocked, openUpgrade, incrementLimit, files.length, isAuthenticated, openAuthGate, saveDocument]);

  // Annule la codification en cours (interrompt la requête réseau).
  const handleCancelAnalyze = useCallback(() => {
    analyzeAbortRef.current?.abort();
  }, []);

  // Reprise automatique : dès que l'utilisateur est connecté, on relance la
  // codification qui était en attente. Délai de stabilisation de session
  // (cf. AUTH_RESUME_SETTLE_MS) pour éviter un 401 après connexion Google.
  useEffect(() => {
    if (pendingAuthLaunch && isAuthenticated) {
      setPendingAuthLaunch(false);
      setTimeout(() => { void handleAnalyze(); }, AUTH_RESUME_SETTLE_MS);
    }
  }, [pendingAuthLaunch, isAuthenticated, handleAnalyze]);

  const runExport = useCallback(async (text: string) => {
    await exportToExcel({
      text,
      verbatimLabels: collectVerbatimLabels(),
      filename: `qualyse_codification_${new Date().toISOString().slice(0, 10)}.xlsx`,
    });
  }, [collectVerbatimLabels]);

  const retryExportFromCache = useCallback(async () => {
    setIsExporting(true);
    try {
      // 1) Tente d'abord avec le résultat encore en mémoire (cas le plus fréquent)
      const localText = result;
      if (localText) {
        await runExport(localText);
        toast.success('Fichier Excel téléchargé.');
        return;
      }
      // 2) Fallback : récupère depuis le cache serveur (utile après refresh)
      const res = await fetch('/api/coding-cache/last');
      if (!res.ok) {
        throw new Error("Aucune codification récente en cache. Relancez la codification.");
      }
      const { text } = await res.json() as { text?: string };
      if (!text) throw new Error("Cache vide. Relancez la codification.");
      await runExport(text);
      toast.success('Fichier Excel téléchargé (récupéré depuis le cache).');
    } catch (err) {
      toast.error(friendlyError(err, 'export'));
    } finally { setIsExporting(false); }
  }, [result, runExport]);

  const handleDownloadExcel = useCallback(async () => {
    if (!result) return;
    setIsExporting(true);
    try {
      await runExport(result);
      toast.success('Fichier Excel téléchargé (3 feuilles : Codification, Grille, Fréquences).');
    } catch (err) {
      const isRetryable =
        err instanceof ExportExcelError &&
        (err.code === 'MATRIX_EXTRACTION_FAILED' || err.code === 'MATRIX_EMPTY');
      if (isRetryable) {
        toast.error(
          "Le fichier généré est incomplet. Cliquez sur « Relancer l'export » pour réessayer sans frais supplémentaires.",
          {
            duration: 15000,
            action: {
              label: "Relancer l'export",
              onClick: () => { void retryExportFromCache(); },
            },
          },
        );
      } else {
        toast.error(friendlyError(err, 'export'));
      }
    }
    finally { setIsExporting(false); }
  }, [result, runExport, retryExportFromCache]);

  const handleRerunWithEditedCodes = useCallback(async () => {
    const verbatims = collectVerbatims();
    if (verbatims.length === 0) { toast.error('Aucun verbatim disponible pour relancer l\'analyse.'); return; }
    if (!editedCodes.trim()) { toast.error('Les codes modifiés sont vides.'); return; }
    setIsLoading(true); setIsEditingCodes(false); setResult('');
    try {
      const response = await fetch('/api/analyze', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'coding', verbatims, approach: 'deductive', themeCount, deductiveGrid: editedCodes.trim() }) });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null) as { error?: string } | null;
        throw new Error(errorData?.error || `Erreur serveur (${response.status})`);
      }
      const reader2 = response.body!.getReader();
      const decoder2 = new TextDecoder();
      let fullText2 = '';
      while (true) {
        const { done, value } = await reader2.read();
        if (done) break;
        fullText2 += decoder2.decode(value, { stream: true });
      }
      fullText2 += decoder2.decode();
      if (fullText2.trim()) { setResult(fullText2); toast.success('Analyse relancée avec les codes modifiés.'); }
      else throw new Error('Réponse vide du serveur.');
    } catch (error) { toast.error(friendlyError(error, 'processing')); }
    finally { setIsLoading(false); }
  }, [collectVerbatims, editedCodes, themeCount]);

  // Confirmation avant « Nouveau projet » — la réinitialisation efface le
  // brouillon (localStorage) sans filet : on exige une validation explicite.
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleReset = useCallback(() => {
    setFiles([]); setPastedText(''); setApproach('inductive'); setDeductiveGrid('');
    setThemeCount('libre'); setResult(''); setGuideFile(null);
    setIsEditingCodes(false); setEditedCodes(''); setIsLoading(false); setStep(1); setUploadProgress(null);
    setRestoredFromCache(false);
    clearPersistedResult(RESULT_STORAGE_KEY, LEGACY_STORAGE_KEY);
    toast.info('Formulaire réinitialisé.');
  }, []);

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto w-full max-w-4xl" style={{ position: 'relative' }}>

      {/* BLOC 1 */}
      {!compact && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: M_BG, border: `1.5px solid ${M_COLOR}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Tags size={24} color={M_COLOR} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.1em', color: M_COLOR, textTransform: 'uppercase', marginBottom: 2 }}>MODULE 03</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.3px', lineHeight: 1.2 }}>Codification thématique</div>
            <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>Analysez vos verbatims avec Qualyse : approche inductive, déductive ou mixte.</div>
          </div>
        </div>
      )}

      {/* STEPPER */}
      <div className="qly-stepper" style={{ display: 'flex', gap: 6, marginBottom: 20, alignItems: 'stretch' }}>
        {M_STEPS.map((s, i) => {
          const n = i + 1;
          const isActive = step === n;
          const isDone = step > n;
          return (
            <div key={n} className="qly-step-item" style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
              {i > 0 && (
                <span className="qly-step-sep" style={{ padding: '0 3px', fontSize: 24, color: '#BBBBBB', fontWeight: 900, flexShrink: 0, userSelect: 'none', lineHeight: 1 }}>›</span>
              )}
              <div
                onClick={() => isDone && setStep(n)}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px', borderRadius: 10, minWidth: 0,
                  border: isActive ? `2px solid ${M_COLOR}` : isDone ? `1.5px solid ${M_COLOR}55` : '1.5px solid #DEDEDE',
                  background: isActive ? M_BG : isDone ? `${M_COLOR}08` : 'white',
                  boxShadow: isActive ? `0 4px 14px ${M_COLOR}35` : 'none',
                  cursor: isDone ? 'pointer' : 'default', transition: 'all 0.2s',
                }}
              >
                <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, background: (isActive || isDone) ? M_COLOR : 'white', border: (isActive || isDone) ? 'none' : '1.5px solid #C0C0C0', color: (isActive || isDone) ? 'white' : '#666' }}>
                  {isDone ? '✓' : n}
                </div>
                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: (isActive || isDone) ? M_COLOR : '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                  <div className="qly-step-hint" style={{ fontSize: 10, color: '#6B7280', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.hint}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* MAIN CARD */}
      <div className="qly-module-card" style={{ background: 'white', borderRadius: 20, border: '1px solid #E5E7EB', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.05)' }}>

        {/* STEP 1 — Données */}
        {step === 1 && (
          <div className="qly-step-content" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Input Mode Toggle */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => setActiveInputMode('upload')} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', border: `2px solid ${M_COLOR}`, background: activeInputMode === 'upload' ? M_COLOR : '#fff', color: activeInputMode === 'upload' ? '#fff' : M_COLOR, transition: 'all .15s' }}>
                <Upload size={14} />Importer des fichiers
              </button>
              <button onClick={() => setActiveInputMode('paste')} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', border: `2px solid ${M_COLOR}`, background: activeInputMode === 'paste' ? M_COLOR : '#fff', color: activeInputMode === 'paste' ? '#fff' : M_COLOR, transition: 'all .15s' }}>
                <ClipboardPaste size={14} />Coller du texte
              </button>
              <span
                aria-disabled="true"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'default', border: '1px dashed #9CA3AF', background: '#F9FAFB', color: '#6B7280', userSelect: 'none' }}
              >
                ⚡ Codifier 10+ entretiens en une seule session
              </span>
            </div>

            {/* Upload Mode */}
            {activeInputMode === 'upload' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div
                  style={{ border: `2px dashed ${M_COLOR}`, borderRadius: 10, background: M_BG, padding: '32px 24px', textAlign: 'center', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop} onDragOver={handleDragOver}
                >
                  <Upload size={28} color={M_COLOR} />
                  <p style={{ fontSize: 14, fontWeight: 600, color: M_COLOR, margin: 0 }}>Glissez-déposez vos fichiers ici</p>
                  <p style={{ fontSize: 13, color: '#374151', margin: 0 }}>ou <span style={{ color: M_COLOR }}>cliquez pour parcourir</span></p>
                  <p style={{ fontSize: 12, color: '#6B7280', margin: '4px 0 0' }}>Formats acceptés : .docx, .pdf, .txt, .md</p>
                  <input ref={fileInputRef} type="file" multiple accept=".txt,.md,.docx,.doc,.pdf,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf" className="hidden" onChange={handleFileSelect} />
                </div>

                {/* Barre de progression groupée */}
                {uploadProgress !== null && (
                  <div style={{ borderRadius: 10, border: `1.5px solid ${uploadAllDone ? '#16a34a' : M_COLOR}`, background: uploadAllDone ? '#f0fdf4' : M_BG, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: uploadAllDone ? '#16a34a' : M_COLOR }}>
                        {uploadAllDone
                          ? `${uploadProgress.total} fichier${uploadProgress.total > 1 ? 's' : ''} prêt${uploadProgress.total > 1 ? 's' : ''} ✓ — vous pouvez lancer l'analyse`
                          : `Téléversement en cours… ${uploadProgress.done}/${uploadProgress.total} fichiers`}
                      </span>
                      {isUploading && <Loader2 size={14} color={M_COLOR} className="animate-spin" />}
                    </div>
                    <div style={{ height: 6, background: '#D1D5DB', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 3, background: uploadAllDone ? '#16a34a' : M_COLOR, width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%`, transition: 'width 0.25s ease' }} />
                    </div>
                  </div>
                )}

                {files.length > 0 && (
                  <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ padding: '8px 14px', borderBottom: '1px solid #F3F4F6', background: '#F9FAFB' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Fichiers importés ({files.length})</span>
                    </div>
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {files.map((file) => (
                        <div key={file.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: '1px solid #F9FAFB' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            <FileText size={14} color={M_COLOR} />
                            <span style={{ fontSize: 12, fontWeight: 500, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                            <span style={{ fontSize: 11, color: '#6B7280', flexShrink: 0 }}>{formatFileSize(file.size)}</span>
                          </div>
                          <button onClick={() => removeFile(file.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                            <X size={14} color="#9CA3AF" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Paste Mode */}
            {activeInputMode === 'paste' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Textarea
                  placeholder="Collez vos verbatims ici. Séparez chaque verbatim par un saut de ligne double…"
                  className="min-h-[200px] font-mono text-sm resize-y"
                  style={{ border: `2px dashed ${M_COLOR}`, borderRadius: 10 }}
                  value={pastedText}
                  onChange={(e) => setPastedText(e.target.value)}
                />
                <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>
                  {pastedText.trim() ? `${pastedText.split(/\n\s*\n/).filter(Boolean).length} verbatim(s) détecté(s) · ${pastedText.length} caractères` : 'Aucun verbatim saisi'}
                </p>
              </div>
            )}

            {/* Summary */}
            {hasVerbatims && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: M_BG, borderRadius: 8 }}>
                <CheckCircle2 size={15} color={M_COLOR} />
                <span style={{ fontSize: 12, fontWeight: 600, color: M_COLOR }}>
                  {collectVerbatims().length} source{collectVerbatims().length > 1 ? 's' : ''} prête{collectVerbatims().length > 1 ? 's' : ''} pour la codification
                </span>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 — Paramètres */}
        {step === 2 && (
          <div className="qly-step-content" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 3, height: 14, borderRadius: 2, background: M_COLOR, flexShrink: 0 }} />
              Étape 2 — Paramètres de codification
            </div>

            {/* Approach */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#111', margin: 0 }}>Approche d&apos;analyse</p>
              <RadioGroup value={approach} onValueChange={(v) => setApproach(v as Approach)} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {APPROACH_OPTIONS.map((option) => (
                  <label key={option.value} style={{ display: 'flex', flexDirection: 'column', gap: 4, borderRadius: 10, border: approach === option.value ? `2px solid ${M_COLOR}` : '2px solid #E5E7EB', padding: '14px', cursor: 'pointer', background: approach === option.value ? M_BG : '#fff', transition: 'all 0.15s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <RadioGroupItem value={option.value} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{option.label}</span>
                    </div>
                    <span style={{ fontSize: 12, color: '#6B7280', paddingLeft: 24 }}>{option.description}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <Separator />

            {/* Theme count */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#111', margin: 0 }}>Nombre de thèmes</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {THEME_COUNT_OPTIONS.map((opt) => (
                  <button key={opt.value} onClick={() => setThemeCount(opt.value)} style={{ padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${themeCount === opt.value ? M_COLOR : '#E5E7EB'}`, background: themeCount === opt.value ? M_BG : '#fff', color: themeCount === opt.value ? M_COLOR : '#555', transition: 'all 0.15s' }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Deductive / Mixed grid */}
            {showDeductiveGrid && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#111', margin: 0 }}>
                  {approach === 'deductive' ? 'Environnement de codage' : 'Codes de référence (optionnel)'}
                </p>
                {/* Guide upload */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>Guide d&apos;entretien</p>
                  {guideFile ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8, background: M_BG, border: `1.5px solid ${M_COLOR}` }}>
                      <FileText size={14} color={M_COLOR} />
                      <span style={{ fontSize: 12, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{guideFile.name}</span>
                      <button onClick={() => setGuideFile(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                        <X size={14} color="#6B7280" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Button variant="outline" size="sm" className="gap-2 w-fit" onClick={() => guideInputRef.current?.click()} disabled={isParsingGuide}>
                        {isParsingGuide ? <Loader2 className="size-4 animate-spin" /> : <FileUp className="size-4" />}
                        {isParsingGuide ? 'Lecture en cours…' : 'Importer un guide d\'entretien'}
                      </Button>
                      <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>PDF, DOCX, TXT acceptés</p>
                      <input ref={guideInputRef} type="file" accept=".pdf,.docx,.doc,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/plain" className="hidden" onChange={handleGuideUpload} />
                    </>
                  )}
                </div>
                {/* Manual codes */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
                    {approach === 'deductive' ? 'Codes prédéfinis' : 'Codes prédéfinis (optionnel)'}
                  </p>
                  <Textarea
                    placeholder={approach === 'mixed' ? 'Saisissez vos codes préétablis…\n\nEx : 1. Satisfaction au travail\n   1.1 Conditions de travail\n2. Stress professionnel' : 'Saisissez la grille de codage à appliquer…\n\nEx : 1. Satisfaction au travail\n   1.1 Conditions de travail'}
                    className="min-h-[140px] font-mono text-sm"
                    style={{ border: '1.5px solid #E5E7EB', borderRadius: 10 }}
                    value={deductiveGrid}
                    onChange={(e) => setDeductiveGrid(e.target.value)}
                  />
                  <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>
                    {approach === 'deductive' ? (guideFile ? 'Le guide importé sera utilisé comme base.' : 'Obligatoire si aucun guide n\'est importé.') : 'Optionnel — les thèmes émergents seront ajoutés à vos codes.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 3 — Résultats */}
        {step === 3 && (
          <div className="qly-step-content" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 3, height: 14, borderRadius: 2, background: M_COLOR, flexShrink: 0 }} />
              Étape 3 — Lancement et résultats
            </div>

            {/* Summary */}
            {!result && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 4 }}>
                {[
                  { label: 'Données', val: hasVerbatims ? `${collectVerbatims().length} source(s)` : 'Aucun verbatim' },
                  { label: 'Approche', val: APPROACH_OPTIONS.find(o => o.value === approach)?.label ?? '—' },
                  { label: 'Thèmes', val: THEME_COUNT_OPTIONS.find(o => o.value === themeCount)?.label ?? '—' },
                  { label: 'Guide', val: guideFile ? guideFile.name : showDeductiveGrid && deductiveGrid.trim() ? 'Codes manuels' : 'Aucun' },
                ].map(({ label, val }) => (
                  <div key={label} style={{ padding: '12px 14px', background: '#FAFAF8', borderRadius: 10, border: '1px solid #E5E7EB' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Plan gratuit — info forfait codification */}
            {!limits.is_pro && !result && !codifBlocked && (
              <div style={{ fontSize: 12, color: '#6B7280', padding: '5px 12px', borderRadius: 6, background: '#F9FAFB', border: '1px solid #E5E7EB', textAlign: 'center' }}>
                📊 Plan gratuit : 1 session jusqu&apos;à {FREE_PLAN.maxEntretiens} entretiens — {FREE_PLAN.codingFlat} crédits par session
              </div>
            )}

            {/* Conseil d'optimisation des crédits — non bloquant */}
            {showOptimizationTip && !result && !codifBlocked && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A', maxWidth: 560, margin: '0 auto' }}>
                <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>💡</span>
                <div style={{ flex: 1, fontSize: 12.5, color: '#78350F', lineHeight: 1.5 }}>
                  Vous pouvez codifier jusqu&apos;à <strong>{tipMaxEntretiens} entretiens</strong> pour le même coût de <strong>{tipFlatCost} crédits</strong>. Voulez-vous ajouter plus d&apos;entretiens ?
                </div>
              </div>
            )}

            {/* Launch button */}
            {!result && (
              codifBlocked ? (
                <UpgradeBlockedCTA
                  variant={codifExhausted ? 'exhausted' : 'locked'}
                  onUpgrade={openUpgrade}
                  align="center"
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: '100%', maxWidth: 520 }}>
                    <CreditCostBanner moduleType="coding" />
                  </div>
                  <button
                    onClick={handleAnalyze}
                    disabled={isLoading || !hasVerbatims}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 32px', borderRadius: 10, border: 'none', background: isLoading || !hasVerbatims ? '#9CA3AF' : M_COLOR, color: '#fff', fontSize: 15, fontWeight: 700, cursor: isLoading || !hasVerbatims ? 'not-allowed' : 'pointer', boxShadow: !isLoading && hasVerbatims ? `0 4px 16px ${M_COLOR}35` : 'none', transition: 'all 0.2s' }}
                  >
                    {isLoading ? <><Loader2 size={18} className="animate-spin" /> Codification en cours…</> : <><Brain size={18} /> Lancer la codification</>}
                  </button>
                  {!hasVerbatims && !isLoading && (
                    <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>Importez vos données à l&apos;étape 1 pour activer la codification</p>
                  )}
                  {isLoading && (
                    <div style={{ width: '100%', maxWidth: 480 }}>
                      <div role="status" aria-live="polite" style={{ marginTop: 12, padding: '14px 16px', background: '#FFFBEB', border: '1.5px solid #F59E0B', borderRadius: 10, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <Loader2 size={18} className="animate-spin" style={{ color: '#B45309', flexShrink: 0, marginTop: 2 }} />
                        <p style={{ fontSize: 13, color: '#78350F', margin: 0, lineHeight: 1.5 }}>
                          <strong>La codification est en cours…</strong><br />
                          Selon le nombre d&apos;entretiens, cela peut prendre jusqu&apos;à 15 minutes. Merci de ne pas fermer cette page.
                        </p>
                      </div>
                      <div style={{ height: 6, background: '#E5E7EB', borderRadius: 6, overflow: 'hidden', marginTop: 12 }}>
                        <div style={{ height: '100%', background: M_COLOR, borderRadius: 6, width: `${codingProgress?.pct ?? 5}%`, transition: 'width 0.6s ease-out' }} />
                      </div>
                      <p style={{ fontSize: 12, color: '#6B7280', textAlign: 'center', marginTop: 8 }}>{codingProgress?.label ?? 'Qualyse analyse vos verbatims…'}</p>
                      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
                        <button
                          type="button"
                          onClick={handleCancelAnalyze}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                          <X size={14} /> Annuler
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            )}

            {/* Historique récent (48 h) — résultats sauvegardés, ré-ouvrables. */}
            {!result && (
              <ModuleHistoryPanel
                history={history}
                accent={M_COLOR}
                noun="codification"
                onReopen={(doc) => { setResult(doc.content); setStep(3); }}
              />
            )}

            {/* Bandeau résultat précédent restauré */}
            {restoredFromCache && result && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, fontSize: 13, color: '#1E40AF' }}>
                <span>Résultat précédent disponible — vous pouvez le télécharger ci-dessous.</span>
                <button onClick={dismissRestoredBanner} style={{ background: 'transparent', border: '1px solid #BFDBFE', color: '#1E40AF', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Effacer</button>
              </div>
            )}

            {/* Results */}
            {result && (() => {
              const summary = computeSummary(result, collectVerbatimLabels().length);
              return (
                <div style={{ background: M_BG, border: `2px solid ${M_COLOR}`, borderRadius: 12, padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: M_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CheckCircle2 size={18} color="white" />
                    </div>
                    <div>
                      <p style={{ fontSize: 15, fontWeight: 700, color: M_COLOR, margin: 0 }}>Codification terminée avec succès</p>
                      <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>Le fichier Excel est prêt à être téléchargé</p>
                    </div>
                    <div style={{ marginLeft: 'auto', background: M_COLOR, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6 }}>Terminé</div>
                  </div>

                  {/* Summary grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
                    {[
                      { label: 'Fichiers traités', value: `${summary.fichiers}/${summary.fichiers}` },
                      { label: 'Thèmes principaux', value: summary.themes },
                      { label: 'Sous-thèmes', value: summary.sousThemes },
                      { label: 'Extraits codifiés', value: summary.extraits },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ borderRadius: 10, border: '1px solid #E5E7EB', background: '#fff', padding: '12px 16px', textAlign: 'center' }}>
                        <p style={{ fontSize: 22, fontWeight: 800, color: M_COLOR, margin: 0 }}>{value}</p>
                        <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>{label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Edit codes */}
                  {approach === 'inductive' && (
                    isEditingCodes ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                        <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>Modifiez les codes générés, puis relancez l&apos;analyse pour les appliquer.</p>
                        <Textarea className="min-h-[320px] font-mono text-sm resize-y" value={editedCodes} onChange={(e) => setEditedCodes(e.target.value)} />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button size="sm" onClick={handleRerunWithEditedCodes} disabled={isLoading || !editedCodes.trim()} style={{ background: M_COLOR, color: '#fff', border: 'none' }}>
                            {isLoading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                            {isLoading ? 'Relance en cours…' : 'Relancer avec ces codes'}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setIsEditingCodes(false)}>Annuler</Button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setEditedCodes(result); setIsEditingCodes(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: M_COLOR, display: 'flex', alignItems: 'center', gap: 4, padding: 0, marginBottom: 16, textDecoration: 'underline' }}>
                        <PenLine size={12} />Modifier les codes avant export
                      </button>
                    )
                  )}

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button onClick={handleDownloadExcel} disabled={isExporting} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 9, border: 'none', background: M_COLOR, color: '#fff', fontSize: 14, fontWeight: 700, cursor: isExporting ? 'wait' : 'pointer', opacity: isExporting ? 0.7 : 1 }}>
                      {isExporting ? <Loader2 size={18} className="animate-spin" /> : <FileSpreadsheet size={18} />}
                      {isExporting ? 'Génération…' : 'Télécharger en Excel (.xlsx)'}
                    </button>
                    <button onClick={() => setShowResetConfirm(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 9, border: `2px solid ${M_COLOR}`, background: '#fff', color: M_COLOR, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                      <RotateCcw size={16} />Nouveau projet
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* BOTTOM NAV */}
        <div className="qly-nav-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 28px', borderTop: '1px solid #E5E7EB', background: '#FAFAF8' }}>
          <button
            disabled={step === 1}
            onClick={() => setStep(s => s - 1)}
            style={{ padding: '10px 20px', background: 'transparent', color: step === 1 ? '#D1D5DB' : '#6B7280', border: `1.5px solid ${step === 1 ? '#E5E7EB' : '#D1D5DB'}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: step === 1 ? 'default' : 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
          >← Retour</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: '#6B7280' }}>Étape {step} sur {M_STEPS.length}</span>
            {result.trim().length > 0 && (
              <button
                type="button"
                className="qly-nav-reset"
                onClick={() => setShowResetConfirm(true)}
                disabled={isLoading}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#fff', color: M_COLOR, border: `1.5px solid ${M_COLOR}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: isLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: isLoading ? 0.5 : 1 }}
              >
                <RotateCcw size={13} /> Nouveau projet
              </button>
            )}
          </div>
          {step < M_STEPS.length ? (
            <button
              disabled={isUploading}
              onClick={() => !isUploading && setStep(s => s + 1)}
              style={{ padding: '10px 24px', background: isUploading ? '#9CA3AF' : M_COLOR, color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: isUploading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8, boxShadow: isUploading ? 'none' : `0 2px 8px ${M_COLOR}40`, transition: 'background 0.15s', opacity: isUploading ? 0.7 : 1 }}
            >Continuer →</button>
          ) : <div style={{ width: 110 }} />}
        </div>
      </div>

      {/* Confirmation « Nouveau projet » — évite la perte accidentelle du résultat. */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Démarrer un nouveau projet ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le résultat actuel sera définitivement perdu. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowResetConfirm(false); handleReset(); }}>
              Démarrer un nouveau projet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
